import { useCallback, useEffect, useState } from "react";
import { ClipboardCheck, CheckCircle2, Circle, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatErr, usePoll } from "@/lib/api";
import { toast } from "sonner";

export function FunderChecklist({ proposalId, content, reloadKey, onReady }) {
  const [c, setC] = useState(null);
  const [paste, setPaste] = useState(null);
  const load = useCallback(() => api.get(`/proposals/${proposalId}/checklist`).then((r) => setC(r.data)).catch(() => {}), [proposalId]);
  useEffect(() => { const t = setTimeout(load, 500); return () => clearTimeout(t); }, [load, content, reloadKey]);
  usePoll(load, c?.status === "running", 3000);
  const [was, setWas] = useState(null);
  useEffect(() => { if (was === "running" && c?.status === "ready") onReady?.(); setWas(c?.status); }, [c?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  const rebuild = async () => {
    try { await api.post(`/proposals/${proposalId}/checklist`, { pasted: paste ?? c?.pasted ?? "" }); setPaste(null); setC({ ...c, status: "running" }); }
    catch (e) { toast.error(formatErr(e)); }
  };
  if (!c) return null;
  const items = c.items || [];
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="funder-checklist-panel">
      <div className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-emerald-600" /><h3 className="font-display text-lg font-bold text-slate-900">Funder checklist</h3>
        {c.status === "ready" && <span className="ml-auto text-xs font-semibold text-slate-600" data-testid="funder-checklist-score">{c.covered} of {c.total} covered</span>}</div>
      {c.status === "running" && <p className="text-sm text-slate-500 mt-3 flex items-center gap-2" data-testid="funder-checklist-running"><Loader2 className="h-4 w-4 animate-spin" />Reading the funder's requirements…</p>}
      {c.status === "error" && <p className="text-sm text-red-600 mt-3">Couldn't build the checklist. Try again.</p>}
      {c.status === "ready" && ["section", "attachment"].map((kind) => {
        const list = items.filter((i) => i.kind === kind);
        if (!list.length) return null;
        return (
          <div key={kind} className="mt-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{kind === "section" ? "Required sections" : "Required attachments"}</div>
            {list.map((i, n) => (
              <div key={i.name} className="flex gap-2 py-1.5 text-sm" data-testid={`checklist-${kind}-${n}`}>
                {i.covered ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> : <Circle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />}
                <div className="min-w-0"><div className={i.covered ? "text-slate-800" : "text-amber-900 font-medium"}>{i.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{i.covered ? i.where : kind === "section" ? "Not in the draft yet" : i.category ? "Add it in the vault or tick it below" : "Not stored in the vault"}</div></div>
              </div>))}
          </div>);
      })}
      {paste === null ? (
        <button onClick={() => setPaste(c.pasted || "")} className="text-xs text-emerald-700 font-medium mt-4 hover:underline" data-testid="funder-checklist-paste-open">{c.pasted ? "Edit the funder's checklist you pasted" : "Paste the funder's own checklist for an exact match"}</button>
      ) : (
        <div className="mt-4 space-y-2">
          <Textarea rows={5} value={paste} onChange={(e) => setPaste(e.target.value)} placeholder="Paste the requirements or checklist from the funder's application page" className="text-sm" data-testid="funder-checklist-paste-input" />
          <div className="flex gap-2"><Button size="sm" onClick={rebuild} className="bg-emerald-600 hover:bg-emerald-700" data-testid="funder-checklist-paste-save"><RefreshCw className="h-4 w-4 mr-2" />Rebuild checklist</Button>
            <Button size="sm" variant="ghost" onClick={() => setPaste(null)} data-testid="funder-checklist-paste-cancel">Cancel</Button></div>
        </div>)}
    </div>
  );
}
